import { CantileverButton } from 'components/CantileverButton'
import { KNOWLEDGE_DOC_TAB_ACTION, useGeckoAnalytics } from 'features/analytics'
import { useChatbotAttachments } from 'features/chatbot'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { createDefaultTab } from 'features/knowledgeDocumentViewer/hooks/useTabState'
import { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { Plus, X } from 'lucide-react'
import { useCallback } from 'react'
import styled, { css, useTheme } from 'styled-components'

const TabsContainer = styled.div`
  width: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem 0 0.5rem;
  gap: 0.125rem;
  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const TabTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 5.25rem;
`

const CloseButton = styled(CantileverButton)`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  visibility: hidden;
`

const AddTabButton = styled(CantileverButton)`
  margin-left: 0.375rem;
`

const Tab = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${(props) =>
    props.$isActive
      ? props.theme.forecast.cardBackground
      : props.theme.forecast.cardGroupBackground};
  border: 0.0625rem solid
    ${(props) =>
      props.$isActive
        ? props.theme.forecast.primary
        : props.theme.forecast.border};
  border-radius: 0.25rem;
  user-select: none;
  white-space: nowrap;
  font-size: 0.875rem;
  color: ${(props) =>
    props.$isActive
      ? props.theme.forecast.defaultText
      : props.theme.forecast.secondaryText};
  max-width: 24rem;

  &:hover {
    background-color: ${(props) => props.theme.forecast.cardBackground};
    border-color: ${(props) =>
      props.$isActive
        ? props.theme.forecast.primary
        : props.theme.forecast.secondaryText};

    ${CloseButton} {
      visibility: visible;
    }

    ${TabTitle} {
      width: calc(100% - 2rem);
    }
  }

  ${({ $isActive }) =>
    $isActive
      ? css`
          ${CloseButton} {
            visibility: visible;
          }

          ${TabTitle} {
            width: calc(100% - 2rem);
          }
        `
      : css`
          &:hover {
            background-color: ${(props) =>
              props.theme.forecast.hoverGhostButton};
            color: ${(props) => props.theme.forecast.defaultText};
          }

          &:active {
            background-color: ${(props) =>
              props.theme.forecast.activeGhostButton};
            color: ${(props) => props.theme.forecast.defaultText};
          }
        `}
`

export function DocumentTabs() {
  const { openTabs, removeTab, activeTabId, setActiveTabId, addTab } =
    useKnowledgeDocumentUXContext()
  const { systemDocumentEntries } = useKnowledgeDocumentDataContext()
  const { trackEvent } = useGeckoAnalytics()
  const { removeAttachment } = useChatbotAttachments()

  const handleTabClick = (tabId: string) => {
    const tab = openTabs.find((t) => t.id === tabId)
    if (tab) {
      trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
        action: 'switch',
        tab_id: tabId,
        document_id: getTabDocumentId(tab),
        total_tabs: openTabs.length,
      })
      setActiveTabId(tabId)
    }
  }

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation()
    const closedTab = openTabs.find((t) => t.id === tabId)
    const isClosingActiveTab = activeTabId === tabId
    const closedTabDocId = closedTab ? getTabDocumentId(closedTab) : ''

    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'close',
      tab_id: tabId,
      document_id: closedTabDocId,
      total_tabs: openTabs.length,
      was_active: isClosingActiveTab,
    })

    if (closedTabDocId) {
      removeAttachment(closedTabDocId)
    }

    removeTab(tabId)
  }

  const handleAddTab = useCallback(() => {
    const newTab: DocumentTab = createDefaultTab()
    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'add',
      tab_id: newTab.id,
      total_tabs: openTabs.length + 1,
    })
    addTab(newTab)
  }, [addTab, openTabs.length, trackEvent])

  const getTabDocumentId = (tab: DocumentTab): string => {
    const currentItem = tab.history[tab.currentIndex]
    return currentItem.type === 'document' ? currentItem.documentId : ''
  }

  const getReportName = useCallback(
    (tab: DocumentTab): string => {
      const currentItem = tab.history[tab.currentIndex]

      if (
        currentItem.type === 'search' &&
        currentItem.linkedDocuments !== undefined
      ) {
        return `References to ${currentItem.linkedDocuments.referenceText}`
      }

      const documentId = getTabDocumentId(tab)
      if (!documentId) return 'New Tab'
      return (
        systemDocumentEntries.find((entry) => entry.entry.id === documentId)
          ?.entry.data.document_title ?? 'New Tab'
      )
    },
    [systemDocumentEntries]
  )

  const theme = useTheme()

  return (
    <TabsContainer>
      {openTabs.map((tab) => (
        <Tab
          key={tab.id}
          $isActive={activeTabId === tab.id}
          onClick={() => handleTabClick(tab.id)}>
          <TabTitle>{getReportName(tab)}</TabTitle>
          <CloseButton
            iconOnly
            ghost
            size='small'
            onClick={(e) => handleCloseTab(e, tab.id)}
            aria-label='Close tab'>
            <X size={18} color={theme.forecast.secondaryText} />
          </CloseButton>
        </Tab>
      ))}
      <AddTabButton
        iconOnly
        ghost
        size='small'
        onClick={handleAddTab}
        title='Add new tab'
        aria-label='Add new tab'>
        <Plus size={18} color={theme.forecast.secondaryText} />
      </AddTabButton>
    </TabsContainer>
  )
}
